import {createSelector} from '@reduxjs/toolkit'

import {includes} from '@shared/helpers/collection'
import {eq, isEmpty} from '@shared/helpers/lang'

import {ExposureArea, ExposureAreaType, NormalPayMethods, PayMethod, PayMethodType} from '@web/core/constants'

import {RootState} from './interfaces'

/** 결제 수단 Selector */
export const paymentSelector = (state: RootState) => state.paymentMethod

/** 나중에결제 결제구분 */
export const payNextMethodSelector = (state: RootState) => state.paymentMethod.selected.payNextMethod

/** 결제수단 선택 info selector */
export const payMethodInfoSelector = createSelector(
    (state: RootState) => state.paymentMethod.selected,
    ({payMethods}) => {
        return {
            /** 선택된 결제수단 (포인트 제외) */
            selectedPayMethod: (function () {
                const usablePayMethod: PayMethodType[] = [
                    PayMethod.BANK_EASY_PAY,
                    PayMethod.CARD,
                    PayMethod.CARD_EASY_PAY,
                    PayMethod.MOBILE,
                    PayMethod.MOBILE_EASY_PAY,
                    PayMethod.VIRTUAL_ACCOUNT,
                    PayMethod.CULTURE_LAND,
                    PayMethod.HAPPY_MONEY,
                    PayMethod.MONEY_TOKEN,
                    PayMethod.WELFARE_CARD,
                    PayMethod.PAY_PAL,
                    PayMethod.SAMSUNG_PAY,
                    PayMethod.FAMILY_PAY,
                    PayMethod.APPLE_PAY,
                ]
                return payMethods.find((payMethod) => usablePayMethod.includes(payMethod)) ?? PayMethod.NONE
            })(),
            /** 충전결제 선택 */
            selectedRechargePayMethod: payMethods.includes(PayMethod.RECHARGE_POINT),
            /** 계좌간편결제 선택 */
            selectedEasyBankPayMethod: payMethods.includes(PayMethod.BANK_EASY_PAY),
            /** 카드간편결제 선택 */
            selectedEasyCardPayMethod: payMethods.includes(PayMethod.CARD_EASY_PAY),
            /** 신용카드결제 선택 */
            selectedCreditCardPayMethod: payMethods.includes(PayMethod.CARD),
            /** 모바일 일반결제 선택 */
            selectedMobilePayMethod: payMethods.includes(PayMethod.MOBILE),
            /** 모바일 간편결제 선택 */
            selectedMobileEasyPayMethod: payMethods.includes(PayMethod.MOBILE_EASY_PAY),
            /** 컬쳐랜드 상품권 */
            selectedCultureLandPayMethod: payMethods.includes(PayMethod.CULTURE_LAND),
            /** 해피머니 상품권 */
            selectedHappyMoneyPayMethod: payMethods.includes(PayMethod.HAPPY_MONEY),
            /** 문화누리카드 */
            selectedWelfareCardPayMethod: payMethods.includes(PayMethod.WELFARE_CARD),
            /** 페이팔 */
            selectedPayPalPayMethod: payMethods.includes(PayMethod.PAY_PAL),
            /** 티머니 */
            selectedMoneyTokenPayMethod: payMethods.includes(PayMethod.MONEY_TOKEN),
            /** 무통장 입금 */
            selectedVirtualAccountPayMethod: payMethods.includes(PayMethod.VIRTUAL_ACCOUNT),
            /** 삼성페이 */
            selectedSamsungPayPayMethod: payMethods.includes(PayMethod.SAMSUNG_PAY),
            /** 애플페이 */
            selectedApplePayPayMethod: payMethods.includes(PayMethod.APPLE_PAY),
            /** 일반결제 여부 */
            selectedGeneralPayMethod: payMethods.some((payMethod) => NormalPayMethods.includes(payMethod)),
            /** 페밀리페이 선택 */
            selectedFamilyPay: payMethods.includes(PayMethod.FAMILY_PAY),
            /** None 선택 */
            selectedNonePayMethod: payMethods.includes(PayMethod.NONE),
        }
    },
)

/**
 * 각 결제수단 별 이용 가능 여부
 * - 서버에서 이용 가능 여부를 판단하고 결정한 응답에 의해 영향을 받습니다.
 * - 사용자의 선택에 의해서 가능 여부가 바뀔 수 없습니다.
 * - 카드혜택목록 존재 시 카드 결제만 가능하며, 쿠폰, 네이버페이 포인트 사용불가 (예약 주문서에서 판단 필요)
 */
export const payMethodAvailableSelector = createSelector(
    (state: RootState) => state.paymentMethod.availableTypes,
    (availableTypes) => {
        const hasPayMethod = (payMethod: PayMethodType): boolean => availableTypes.includes(payMethod)

        /** 일반결제수단 목록 */
        const availableGeneralPayMethod: PayMethodType[] = [
            PayMethod.CARD,
            PayMethod.MOBILE,
            PayMethod.MOBILE_EASY_PAY,
            PayMethod.VIRTUAL_ACCOUNT,
            PayMethod.CULTURE_LAND,
            PayMethod.HAPPY_MONEY,
            PayMethod.MONEY_TOKEN,
            PayMethod.WELFARE_CARD,
            PayMethod.PAY_PAL,
            PayMethod.APPLE_PAY,
        ].filter(hasPayMethod)

        /**
         * REWARD_POINT(적립포인트), CHARGE_POINT(충전포인트) 중 하나라도
         * 사용 가능 결제수단에서 제외되는 경우
         * 포인트 영역 미노출 & 결제수단 충전결제 영역 미노출 처리
         * @see https://oss.fin.navercorp.com/payOrder/order-qa/issues/4589#issuecomment-758748
         * @Deprecated 단걸결제에서는 적립 OR 충전중 하나만 사용이 가능하여도 포인트 사용 가능 > app 으로 로직 이관
         */
        const isRewardPointAvailable = hasPayMethod(PayMethod.REWARD_POINT)
        const isChargePointAvailable = hasPayMethod(PayMethod.CHARGE_POINT)
        const isRechargePointAvailable = isChargePointAvailable && hasPayMethod(PayMethod.RECHARGE_POINT)
        const isPayPointAvailable = isRewardPointAvailable && isChargePointAvailable
        return {
            /** 이용가능한 결제수단 목록 */
            availablePayMethodTypes: availableTypes,
            /** 이용가능한 일반결제수단 목록 */
            usableNormalPayMethods: availableGeneralPayMethod,
            isRewardPointAvailable,
            isChargePointAvailable,
            isRechargePointAvailable,
            isPayPointAvailable,
            isEasyPayBankAvailable: hasPayMethod(PayMethod.BANK_EASY_PAY),
            isEasyPayCardAvailable: hasPayMethod(PayMethod.CARD_EASY_PAY),
            isGeneralPaymentAvailable: !isEmpty(availableGeneralPayMethod),
            isVirtualAccountAvailable: hasPayMethod(PayMethod.VIRTUAL_ACCOUNT),
            isSamsungPayAvailable: hasPayMethod(PayMethod.SAMSUNG_PAY),
            isFamilyPaymentAvailable: hasPayMethod(PayMethod.FAMILY_PAY),
            isApplePayPaymentAvailable: hasPayMethod(PayMethod.APPLE_PAY),
            isMobileGeneralPayAvailable: hasPayMethod(PayMethod.MOBILE),
            isMobileEasyPayAvailable: hasPayMethod(PayMethod.MOBILE_EASY_PAY),
        }
    },
)

/** 선택된 결제 수단별 안내 Selector */
export const payMethodStatementByPayMethod = createSelector(
    payMethodInfoSelector,
    (state: RootState) => state.paymentMethod.statements,
    (state: RootState) => state.paymentMethod.errorExposureArea,
    ({selectedPayMethod, selectedRechargePayMethod}, payMethodStatementsByExposureAreaType, errorExposureArea) => {
        return {
            /** 결제수단 별 노출 문구 */
            payMethodStatementsByExposureAreaType,
            /** 선택된 결제수단에 따른 홍보문구 노출 */
            selectedPaymentStatement: (function () {
                switch (selectedPayMethod) {
                    case PayMethod.BANK_EASY_PAY:
                        return payMethodStatementsByExposureAreaType[ExposureArea.BANK_EASY_PAY] ?? null
                    case PayMethod.CARD_EASY_PAY:
                        return payMethodStatementsByExposureAreaType[ExposureArea.CARD_EASY_PAY] ?? null
                    case PayMethod.CARD:
                        return payMethodStatementsByExposureAreaType[ExposureArea.CARD] ?? null
                    case PayMethod.MOBILE:
                        return payMethodStatementsByExposureAreaType[ExposureArea.MOBILE] ?? null
                    case PayMethod.VIRTUAL_ACCOUNT:
                        return payMethodStatementsByExposureAreaType[ExposureArea.VIRTUAL_ACCOUNT] ?? null
                    default:
                        // 복합결제 확인 필요
                        if (selectedRechargePayMethod) {
                            return payMethodStatementsByExposureAreaType[ExposureArea.CHARGE_POINT] ?? null
                        }
                }
                return null
            })(),
            /** 선택된 결제 수단별 장애 안내 메시지 */
            selectedPaymentErrorStatement: (function () {
                if (selectedRechargePayMethod) {
                    return payMethodStatementsByExposureAreaType[ExposureArea.CHARGE_POINT] ?? null
                }

                if (selectedPayMethod && includes(selectedPayMethod, errorExposureArea)) {
                    return payMethodStatementsByExposureAreaType[selectedPayMethod as ExposureAreaType] ?? null
                }
                return null
            })(),
        }
    },
)

export const paymentMethodLoadSceneSelector = createSelector(
    (state: RootState) => state.paymentMethod.scene,
    (scene) => {
        const eqScene = eq(scene)
        return {
            isIdle: eqScene('IDLE'),
            isPending: eqScene('PENDING'),
            isFetching: eqScene('FETCHING'),
            isResolved: eqScene('RESOLVED'),
            isRejected: eqScene('REJECTED'),
        }
    },
)
